// Utils
import {
    getImageProp,
    getImageError,
    getFileProp,
    getShowMedia,
    getImageColors,
    getFileUploadedTimestamp,
} from '../../../../common/elements/utils/elementPropertyUtils';
import { getData } from '../../attachments/attachmentsSelector';
import fileCanShowMedia from './fileCanShowMedia';
import fileGetMediaPlayerType, { isAudioFile } from './fileGetMediaPlayerType';
import { prop } from '../../../../common/utils/immutableHelper';

// Constants
import { getTimestamp, TIMES } from '../../../../common/utils/timeUtil';

// Types
import type * as Immutable from 'immutable';
import type { ImMNElement } from '../../../../common/elements/elementModelTypes';

type FileElementArgs = {
    element: ImMNElement;
    attachment?: Immutable.Map<string, unknown>;
};

export const getFileData = ({ element, attachment }: FileElementArgs) => getFileProp(element) || getData(attachment);
export const hasFileData = ({ element, attachment }: FileElementArgs): boolean =>
    !!getFileData({ element, attachment });

export const hasPreviewImage = ({ element }: { element: ImMNElement }): boolean =>
    !!getImageProp(element) && !getImageError(element);

export const canReplaceFilePreviewImage = (element: ImMNElement): boolean =>
    !isAudioFile(element) || hasPreviewImage({ element });

export const getIsFileMediaVisible = ({ element, attachment }: FileElementArgs): boolean =>
    (hasPreviewImage({ element }) || !!fileGetMediaPlayerType(element)) &&
    hasFileData({ element, attachment }) &&
    getShowMedia(element) &&
    fileCanShowMedia(element, attachment);

export const getFileDataFilename = prop('filename');
export const getFileDataType = prop('type');
export const getFileDataSize = prop('size');

export const getFileIsAwaitingPreview = ({ element, attachment }: FileElementArgs): boolean => {
    const imageData = getImageProp(element);

    return !imageData && fileCanShowMedia(element, attachment);
};

// True while the file is being processed server-side (generating a preview, or extracting a
// colour palette from one). Upload/download byte progress is shown by FileTransferToast, so the
// file card only surfaces this processing state.
export const getFileIsProcessing = ({ element, attachment }: FileElementArgs): boolean => {
    // Stop showing the processing state 5 minutes after upload so a stuck preview can't loop forever.
    const fiveMinutesAgo = getTimestamp() - TIMES.MINUTE * 5;
    if (getFileUploadedTimestamp(element) < fiveMinutesAgo) return false;

    // A (non-error) preview image exists but its colour palette hasn't been extracted yet.
    // Error images never get colours, so excluding them stops a failed preview showing a perpetual bar.
    const awaitingColors =
        !!getImageProp(element) &&
        !getImageError(element) &&
        !getImageColors(element) &&
        fileCanShowMedia(element, attachment);

    return awaitingColors || getFileIsAwaitingPreview({ element, attachment });
};
